export type UserRole =
  "Admin" | "Personaler" | "Teamleiter" | "Mitarbeiter" | "SuperAdmin";

export const ROLES = {
  ADMIN: "Admin" as const,
  MANAGER: "Personaler" as const,
  TEAMLEAD: "Teamleiter" as const,
  EMPLOYEE: "Mitarbeiter" as const,
  SUPER_ADMIN: "SuperAdmin" as const,
};

type TaskAccessShape = {
  teamleads?: unknown[] | null;
  createdById?: string | number | null;
  responsible?: unknown[] | null;
  representatives?: unknown[] | null;
  editableByUsers?: unknown[] | null;
};

type DeletableTaskShape = {
  createdById?: string | number | null;
  isProcessed?: boolean | null;
};

export const USER_MANAGEMENT_ROLES: UserRole[] = [ROLES.ADMIN, ROLES.MANAGER];

// Company Admins manage their own third-party connection and API token.
export const DATA_SOURCE_ROLES: UserRole[] = [ROLES.ADMIN];

// Field mappings apply across companies and remain platform administration.
export const DATA_SOURCE_MAPPING_ROLES: UserRole[] = [ROLES.SUPER_ADMIN];

export const USER_OVERVIEW_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
];

export const TASK_MANAGEMENT_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
];

// Everybody working in a company may create a single task and give it to
// anybody there. Series: see ``canCreateSeries`` (a Mitarbeiter needs the
// tick "Darf Aufgabenserien anlegen"). The task management list stays with
// the roles above.
export const TASK_CREATE_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
  ROLES.EMPLOYEE,
];

// The series pages (route guards). A Mitarbeiter opens the series they
// may change even without the tick; the backend decides per series.
export const SERIES_ROLES: UserRole[] = TASK_CREATE_ROLES;

export const PLANNER_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
  ROLES.EMPLOYEE,
];

export const COST_CENTER_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
];

export const EVALUATE_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
  ROLES.EMPLOYEE,
];

// Escalation notice of the urgent banner (2026-11-02): urgent work nobody
// is responsible for, for the people who hand work out. Mirrors
// ``unownedUrgentOccurrences`` (backend tasks/escalation.py).
export const ESCALATION_NOTICE_ROLES: UserRole[] = [
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.TEAMLEAD,
];

export const seesEscalationNotice = (role?: UserRole | null): boolean => {
  return Boolean(role && ESCALATION_NOTICE_ROLES.includes(role));
};

export const CONTRACT_MANAGEMENT_ROLES: UserRole[] = [ROLES.SUPER_ADMIN];

export const COMPANY_MANAGEMENT_ROLES: UserRole[] = [ROLES.SUPER_ADMIN];

// A company's own master data, contracts and cancellation are managed by
// that company's Admin. The Super Admin is not included: they have no
// company of their own and act across tenants on the companies pages.
export const OWN_COMPANY_ROLES: UserRole[] = [ROLES.ADMIN];

export const canManageOwnCompany = (role?: UserRole | null): boolean => {
  return Boolean(role && OWN_COMPANY_ROLES.includes(role));
};

export const isSuperAdmin = (role?: UserRole | null): boolean => {
  return role === ROLES.SUPER_ADMIN;
};

const isSelf = (
  currentUserId?: string | null,
  targetUserId?: string | null,
): boolean => {
  return Boolean(
    currentUserId && targetUserId && currentUserId === targetUserId,
  );
};

const taskContainsUser = (
  value: unknown[] | null | undefined,
  userId?: string | null,
) => {
  if (!userId || !Array.isArray(value)) return false;
  return value.map(String).includes(String(userId));
};

export const canManageUsers = (role?: UserRole | null): boolean => {
  return Boolean(role && USER_MANAGEMENT_ROLES.includes(role));
};

export const canAccessUserOverview = (role?: UserRole | null): boolean => {
  return Boolean(role && USER_OVERVIEW_ROLES.includes(role));
};

export const canCreateTask = (role?: UserRole | null): boolean => {
  return Boolean(role && TASK_MANAGEMENT_ROLES.includes(role));
};

/** May open "Neue Aufgabe". ``canCreateTask`` is the managing right: the
 * task management list and other people's tasks. */
export const canCreateOwnTasks = (role?: UserRole | null): boolean => {
  return Boolean(role && TASK_CREATE_ROLES.includes(role));
};

/** May open the series pages and change series or runs where the backend
 * reports ``canEdit``: a Mitarbeiter changes the series they created, not
 * others. Creating a new series is ``canCreateSeries``. */
export const canUseSeriesPages = (role?: UserRole | null): boolean => {
  return Boolean(role && SERIES_ROLES.includes(role));
};

/** May create a series (new, or a single task converted into one). Admin,
 * Personaler and Teamleiter always; a Mitarbeiter only with the tick "Darf
 * Aufgabenserien anlegen" (since 2026-11-03). Mirrors
 * ``can_create_series`` in backend/tasks/series_services.py. */
export const canCreateSeries = (
  role?: UserRole | null,
  mayCreateSeries?: boolean | null,
): boolean => {
  if (!role || !SERIES_ROLES.includes(role)) return false;
  if (role === ROLES.EMPLOYEE) return Boolean(mayCreateSeries);
  return true;
};

const isCreator = (currentUserId?: string | null, task?: TaskAccessShape | null) =>
  Boolean(currentUserId) && String(task?.createdById ?? "") === String(currentUserId);

export const canAssignTasks = (role?: UserRole | null): boolean => {
  return Boolean(role && TASK_MANAGEMENT_ROLES.includes(role));
};

export const canAccessPlanner = (role?: UserRole | null): boolean => {
  return Boolean(role && PLANNER_ROLES.includes(role));
};

export const canAccessCostCenters = (role?: UserRole | null): boolean => {
  return Boolean(role && COST_CENTER_ROLES.includes(role));
};

export const canManageDataSource = (role?: UserRole | null): boolean => {
  return Boolean(role && DATA_SOURCE_ROLES.includes(role));
};

export const canAccessEvaluate = (role?: UserRole | null): boolean => {
  return Boolean(role && EVALUATE_ROLES.includes(role));
};
/** Urlaub / Krank cards in "Planen", mirroring accounts/absence_cards.py:
 * Admin and Personaler set both, a Teamleiter sets Urlaub for the people of
 * his departments (the planner only offers those). Admin and Personaler
 * record their own absence too, nobody else does. */
export const absenceKindsFor = (
  role?: UserRole | null,
  isOwnPlan = false,
): ("VACATION" | "SICK")[] => {
  if (role === ROLES.ADMIN || role === ROLES.MANAGER) return ["VACATION", "SICK"];
  if (isOwnPlan) return [];
  if (role === ROLES.TEAMLEAD) return ["VACATION", "SICK"];
  return [];
};

export const canSetAbsence = (
  role?: UserRole | null,
  isOwnPlan = false,
): boolean => absenceKindsFor(role, isOwnPlan).length > 0;

export const canAccessArbeiten = (role?: UserRole | null): boolean => {
  return Boolean(role && EVALUATE_ROLES.includes(role));
};

export const canViewTaskDetail = (
  role?: UserRole | null,
  currentUserId?: string | null,
  task?: TaskAccessShape | null,
): boolean => {
  if (!task) return false;
  if (canCreateTask(role)) return true;
  return (
    isCreator(currentUserId, task) ||
    taskContainsUser(task.teamleads, currentUserId) ||
    taskContainsUser(task.responsible, currentUserId) ||
    taskContainsUser(task.representatives, currentUserId) ||
    taskContainsUser(task.editableByUsers, currentUserId)
  );
};

export const canEditTaskMetadata = (
  role?: UserRole | null,
  currentUserId?: string | null,
  task?: TaskAccessShape | null,
): boolean => {
  if (!task) return false;
  if (canCreateTask(role)) return true;
  return (
    isCreator(currentUserId, task) ||
    taskContainsUser(task.teamleads, currentUserId) ||
    taskContainsUser(task.editableByUsers, currentUserId)
  );
};

export const canEditTaskGuide = (
  role?: UserRole | null,
  currentUserId?: string | null,
  task?: TaskAccessShape | null,
): boolean => {
  return canEditTaskMetadata(role, currentUserId, task);
};

export const canDeleteTask = (
  role?: UserRole | null,
  currentUserId?: string | null,
  task?: DeletableTaskShape | null,
): boolean => {
  if (!task) return false;
  if (canCreateTask(role)) return true;
  return String(task.createdById ?? "") === String(currentUserId ?? "");
};

export const canEditTaskContent = (
  role?: UserRole | null,
  currentUserId?: string | null,
  task?: TaskAccessShape | null,
): boolean => {
  if (!task) return false;
  if (canCreateTask(role)) return true;
  return (
    isCreator(currentUserId, task) ||
    taskContainsUser(task.teamleads, currentUserId)
  );
};

export const canDeleteUsers = (role?: UserRole | null): boolean => {
  return Boolean(role && (role === ROLES.ADMIN || role === ROLES.MANAGER));
};

export const canEditTimeEntries = (role?: UserRole | null): boolean => {
  return Boolean(
    role &&
    (role === ROLES.ADMIN || role === ROLES.MANAGER || role === ROLES.TEAMLEAD),
  );
};

export const canViewSalary = (
  role?: UserRole | null,
  currentUserId?: string | null,
  targetUserId?: string | null,
): boolean => {
  return (
    canManageUsers(role) ||
    (role === ROLES.EMPLOYEE && isSelf(currentUserId, targetUserId))
  );
};

export const canEditUser = (role?: UserRole | null): boolean => {
  return canManageUsers(role);
};

export const canViewUserDetail = (
  role?: UserRole | null,
  currentUserId?: string | null,
  targetUserId?: string | null,
): boolean => {
  return (
    canManageUsers(role) ||
    role === ROLES.TEAMLEAD ||
    (role === ROLES.EMPLOYEE && isSelf(currentUserId, targetUserId))
  );
};

/** The check levels of a closed task, see backend tasks/review.py. */
export type ReviewLevel = "responsible" | "teamlead" | "hr" | "admin" | "done";

type ReportRoleInput = {
  role?: UserRole | null;
  userId?: string | number | null;
  responsibleIds?: (string | number | null | undefined)[] | null;
  /** The level the check waits for, from the server (``reviewLevel``). */
  reviewLevel?: string | null;
  /** Whether the viewer checks the task now, from the server. */
  canReview?: boolean | null;
};

export type ReportRoleView = {
  /** Show the answering view: the person works on this task themselves. */
  showEmployeeView: boolean;
  /** Show the review view of Verantwortlich and Teamleiter. */
  showTeamLeadView: boolean;
  /** Show the review view of Personaler and Admin. */
  showHrView: boolean;
  /**
   * The person answers AND reviews the same task, so both views render at
   * once. Sole traders hit this on every task they assign to themselves.
   */
  isSelfReview: boolean;
};

/**
 * Decide which report views a person gets on one task.
 *
 * Answering and reviewing are separate concerns, so they are answered
 * separately rather than by picking a single winning role. Who reviews is
 * decided by the server along the check chain (Verantwortlich, Teamleiter,
 * Personaler/Admin, Admin); the view only follows the level it reports.
 */
export const resolveReportRoleView = ({
  role,
  userId,
  responsibleIds,
  reviewLevel,
  canReview,
}: ReportRoleInput): ReportRoleView => {
  const me = userId != null && userId !== "" ? String(userId) : null;
  const isResponsible =
    me !== null &&
    (responsibleIds ?? []).some((id) => id != null && String(id) === me);

  const reviews = Boolean(canReview);
  const showTeamLeadView =
    reviews && (reviewLevel === "responsible" || reviewLevel === "teamlead");
  const showHrView = reviews && (reviewLevel === "hr" || reviewLevel === "admin");
  // On the level Verantwortlich the responsible person checks work somebody
  // else did; answering it as their own would mix up whose answers they are.
  const checksOthersWork = reviews && reviewLevel === "responsible";
  const showEmployeeView =
    (isResponsible || role === ROLES.EMPLOYEE) && !checksOthersWork;

  return {
    showEmployeeView,
    showTeamLeadView,
    showHrView,
    isSelfReview: showEmployeeView && (showTeamLeadView || showHrView),
  };
};
